import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  selectWithcookieData,
  selectUser
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import '../PayLetterPayment/PayLetterPayment.css';

interface PayLetterFailProps {
  className?: string;
  style?: React.CSSProperties;
  isEditor?: boolean;
  componentProps?: {
    title?: string;
    failMessage?: string;
    retryButtonText?: string;
    cartButtonText?: string;
    homeButtonText?: string;
    [key: string]: any;
  };
  data?: {
    title?: string;
    failMessage?: string;
    retryButtonText?: string;
    cartButtonText?: string;
    homeButtonText?: string;
    [key: string]: any;
  };
}

interface PayLetterFailData {
  code?: string;
  message?: string;
  user_id?: string;
  user_name?: string;
  order_no?: string;
  service_name?: string;
  product_name?: string;
  custom_parameter?: string;
}

/**
 * PayLetter 결제 실패 처리 컴포넌트
 * PayLetter에서 결제 실패/취소 후 리턴되는 페이지
 */
const PayLetterFail: React.FC<PayLetterFailProps> = ({ 
  className = '', 
  style = {}, 
  isEditor = false,
  componentProps = {},
  data = {}
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch() as any;
  
  const user = useSelector(selectUser);
  const withcookieData = useSelector(selectWithcookieData);
  
  const [isLoading, setIsLoading] = useState(true);
  const [failInfo, setFailInfo] = useState<PayLetterFailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // 쿠키에서 값을 읽는 헬퍼 함수
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  };

  // 쿠키 삭제 헬퍼 함수
  const deleteCookie = (name: string) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  // 결제 취소 API 호출 함수
  const cancelCheckout = async (checkoutId: string) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${apiBaseUrl}/api/checkouts/${checkoutId}`, {
        method: 'PATCH',
        headers: {
          'Accept': '*/*',
          'Accept-Language': 'ko-KR,ko;q=0.9',
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        credentials: 'include',
        body: JSON.stringify({ status: 'CANCELED' })
      });
      
      if (!response.ok) {
        console.error('결제 취소 실패:', response.status);
      } else {
        console.log('결제가 성공적으로 취소되었습니다.');
      }
    } catch (error) {
      console.error('결제 취소 중 오류 발생:', error);
    }
  };

  useEffect(() => {
    // 에디터 모드에서는 더미 데이터 사용
    if (isEditor) {
      setFailInfo({
        code: '-1',
        message: componentProps.failMessage || data.failMessage || t('결제가 취소되었거나 실패했습니다.'),
        order_no: 'ORDER_DEMO_12345',
        user_id: 'demo_user',
        user_name: '홍길동',
        service_name: '데모 서비스',
        product_name: '데모 상품'
      });
      setIsLoading(false);
      return;
    }
    
    // URL 파라미터 처리
    try {
      const searchParams = new URLSearchParams(location.search);
      
      // URL 파라미터에서 실패 정보 추출
      const code = searchParams.get('code');
      const message = searchParams.get('message');
      const orderId = searchParams.get('order_no');
      const userId = searchParams.get('user_id');
      const serviceName = searchParams.get('service_name');
      const productName = searchParams.get('product_name');
      const user_name = searchParams.get('user_name');
      
      // 실패 정보 설정
      setFailInfo({
        code: code || '-1',
        message: message || t('결제가 취소되었거나 실패했습니다.'),
        order_no: orderId || '',
        user_id: userId || '',
        service_name: serviceName || '',
        product_name: productName || '',
        user_name: user_name || ''
      });
      
      // 쿠키에서 checkoutId 확인 및 취소 처리
      const checkoutId = getCookie('payletter_checkout_id');
      if (checkoutId) {
        console.log('결제 취소 요청 - checkoutId:', checkoutId);
        cancelCheckout(checkoutId).finally(() => {
          // 취소 요청 후 쿠키 삭제
          deleteCookie('payletter_checkout_id');
        });
      }
      
      setIsLoading(false);
    } catch (error: any) {
      console.error('PayLetter 실패 처리 오류:', error);
      setError(error.message || t('결제 처리 중 오류가 발생했습니다.'));
      setIsLoading(false);
    }
  }, [location.search, isEditor]); // 의존성 배열 수정
  
  const handleRetryPayment = () => {
    // 이전 페이지(결제 페이지)로 돌아가기
    navigate(-1);
  };
  
  const handleGoToCart = () => {
    navigate('/cart');
  };
  
  const handleGoToHome = () => {
    navigate('/');
  };
  
  if (isLoading) {
    return (
      <div className={`payletter-fail-container ${className}`} style={style}>
        <div className="payment-result-card loading">
          <div className="spinner"></div>
          <h2>{t("결제 정보를 처리 중입니다...")}</h2>
          <p>{t("잠시만 기다려주세요.")}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={`payletter-fail-container ${className}`} style={style}>
        <div className="payment-result-card error">
          <div className="error-icon">❌</div>
          <h2>{t("오류가 발생했습니다")}</h2>
          <p>{error}</p>
          <div className="payment-action-buttons">
            <button onClick={handleGoToHome} className="go-home-button">
              {t("홈으로 돌아가기")}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`payletter-fail-container ${className}`} style={style}>
      <div className="payment-result-card error">
        <div className="error-icon">❌</div>
        <h2>{componentProps.title || data.title || t("결제에 실패했습니다")}</h2>
        
        {failInfo && (
          <div className="payment-fail-info">
            <div className="fail-message">
              <p className="main-message">{failInfo.message}</p>
              {failInfo.code && failInfo.code !== '-1' && (
                <p className="error-code">
                  {t("오류 코드")}: {failInfo.code}
                </p>
              )}
            </div>
            
            {failInfo.order_no && (
              <div className="order-info">
                <div className="info-row">
                  <span className="label">{t("주문번호")}:</span>
                  <span className="value">{failInfo.order_no}</span>
                </div>
                {failInfo.user_name && (
                  <div className="info-row">
                    <span className="label">{t("주문자명")}:</span>
                    <span className="value">{failInfo.user_name}</span>
                  </div>
                )}
                {failInfo.product_name && (
                  <div className="info-row">
                    <span className="label">{t("상품명")}:</span>
                    <span className="value">{failInfo.product_name}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="fail-reasons">
              <h4>{t("결제 실패 가능한 원인")}</h4>
              <ul>
                <li>{t("카드 한도 초과 또는 잔액 부족")}</li>
                <li>{t("카드 정보 입력 오류")}</li>
                <li>{t("은행 서버 일시적 장애")}</li>
                <li>{t("사용자가 결제를 취소")}</li>
                <li>{t("네트워크 연결 불안정")}</li>
              </ul>
            </div>
          </div>
        )}
        
        <div className="payment-action-buttons">

          <button onClick={handleGoToHome} className="go-home-button">
            {componentProps.homeButtonText || data.homeButtonText || t("홈으로 돌아가기")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayLetterFail; 